import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { FileText, Clock, Eye, ArrowRight, Loader2, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function BlogListPage() {
  const [articles, setArticles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState(null);

  useEffect(() => {
    fetchArticles();
    fetchTags();
  }, [page, activeTag]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10, status: 'published' };
      if (activeTag) params.tag = activeTag;
      const res = await axios.get(`${API}/blog/articles`, { params });
      setArticles(res.data.articles);
      setTotalPages(res.data.pages);
    } catch {}
    setLoading(false);
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API}/blog/tags`);
      setTags(res.data.tags);
    } catch {}
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="blog-list-page">
      <div className="flex items-center gap-2 mb-1">
        <FileText className="w-5 h-5 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
          BLOG
        </h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Artykuły, poradniki i nowości ze świata okazji</p>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-6" data-testid="blog-tags">
          <button
            onClick={() => { setActiveTag(null); setPage(1); }}
            className={`px-3 py-1 text-xs font-heading uppercase tracking-wider border transition-colors ${
              !activeTag ? 'border-primary text-primary bg-primary/10' : 'border-border/40 text-muted-foreground hover:border-primary/30'
            }`}
          >
            Wszystkie
          </button>
          {tags.map(t => (
            <button
              key={t.name}
              onClick={() => { setActiveTag(t.name); setPage(1); }}
              className={`px-3 py-1 text-xs font-heading uppercase tracking-wider border transition-colors ${
                activeTag === t.name ? 'border-primary text-primary bg-primary/10' : 'border-border/40 text-muted-foreground hover:border-primary/30'
              }`}
            >
              {t.name} ({t.count})
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">Brak artykułów. Admin może dodać pierwszy!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="articles-list">
          {articles.map((article, i) => {
            const coverImg = article.cover_image_local
              ? `${BACKEND}${article.cover_image_local}`
              : article.cover_image_url;

            return (
              <Link
                key={article.id}
                to={`/blog/${article.slug}`}
                className="bg-card border border-border/40 hover:border-primary/30 transition-colors group overflow-hidden"
                data-testid={`article-card-${article.id}`}
              >
                {coverImg && (
                  <div className="h-44 bg-secondary overflow-hidden">
                    <img
                      src={coverImg}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-4">
                  <h2 className="font-heading text-lg font-bold text-white group-hover:text-primary transition-colors line-clamp-2 mb-2">
                    {article.title}
                  </h2>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-3">
                      <span>{article.author_nick}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(article.created_at).toLocaleDateString('pl-PL')}
                      </span>
                    </div>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" /> {article.views}
                    </span>
                  </div>
                  {article.tags?.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap mt-2">
                      {article.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary/80 border border-primary/20">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="bg-secondary text-secondary-foreground rounded-none"
          >
            Poprzednie
          </Button>
          <span className="flex items-center text-sm text-muted-foreground px-3">
            {page} / {totalPages}
          </span>
          <Button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="bg-secondary text-secondary-foreground rounded-none"
          >
            Następne
          </Button>
        </div>
      )}
    </div>
  );
}
